import { useMemo } from 'react';

import { colors } from '@ui';
import * as THREE from 'three';

const generateBoneMap = (point1: THREE.Vector3, point2: THREE.Vector3) => ({
    left_hip__pelvis: {
        start: point2,
        end: point1,
        size: 'medium',
    },
    right_hip__pelvis: {
        start: point2,
        end: point1,
        size: 'medium',
    },
    left_ankle__left_knee: {
        start: point2,
        end: point1,
        size: 'large',
    },
    right_ankle__right_knee: {
        start: point2,
        end: point1,
        size: 'large',
    },
    left_knee__left_hip: {
        start: point2,
        end: point1,
        size: 'large',
    },
    right_knee__right_hip: {
        start: point2,
        end: point1,
        size: 'large',
    },
    left_shoulder__left_elbow: {
        start: point1,
        end: point2,
        size: 'large',
    },
    right_shoulder__right_elbow: {
        start: point1,
        end: point2,
        size: 'large',
    },
    left_shoulder__upper_torso: {
        start: point2,
        end: point1,
        size: 'medium',
    },
    right_shoulder__upper_torso: {
        start: point2,
        end: point1,
        size: 'medium',
    },
    left_ankle__left_heel: {
        start: point1,
        end: point2,
        size: 'small',
    },
    right_ankle__right_heel: {
        start: point1,
        end: point2,
        size: 'small',
    },
    left_elbow__left_wrist: {
        start: point1,
        end: point2,
        size: 'medium',
    },
    right_elbow__right_wrist: {
        start: point1,
        end: point2,
        size: 'medium',
    },
    left_wrist__left_index_base: {
        start: point1,
        end: point2,
        size: 'small',
    },
    right_index_base__right_wrist: {
        start: point2,
        end: point1,
        size: 'small',
    },
    left_big_toe__left_heel: {
        start: point2,
        end: point1,
        size: 'medium',
    },
    right_big_toe__right_heel: {
        start: point2,
        end: point1,
        size: 'medium',
    },
    left_heel__left_big_toe: {
        start: point1,
        end: point2,
        size: 'medium',
    },
    right_heel__right_big_toe: {
        start: point1,
        end: point2,
        size: 'medium',
    },
    upper_torso__pelvis: {
        start: point2,
        end: point1,
        size: 'large',
    },
    head__upper_torso: {
        start: point2,
        end: point1,
        size: 'medium',
    },
});

const geometryMap = {
    large: { radius: 0.035, openEnded: true },
    medium: { radius: 0.02, openEnded: true },
    small: { radius: 0.01, openEnded: false },
};

export const BoneMesh = ({
    point1,
    point2,
    name,
    isFrozen = false,
    isTranslucent = false,
    isComparison = false,
}: {
    point1: THREE.Vector3;
    point2: THREE.Vector3;
    name: string;
    isFrozen?: boolean;
    isTranslucent?: boolean;
    isComparison?: boolean;
}) => {
    const boneMap = useMemo(() => generateBoneMap(point1, point2), [point1, point2]);

    if (!boneMap[name as keyof typeof boneMap]) {
        return null;
    }

    const { start, end, size } = boneMap[name as keyof typeof boneMap];
    const { radius, openEnded } = geometryMap[size as keyof typeof geometryMap];

    const direction = end.clone().sub(start);
    const length = direction.length();
    const center = start.clone().add(end).multiplyScalar(0.5);

    const yAxis = direction.normalize();
    const xAxis = new THREE.Vector3().crossVectors(yAxis, new THREE.Vector3(1, 0, 0)).normalize();

    const zAxis = new THREE.Vector3().crossVectors(xAxis, yAxis).normalize();
    const rotMatrix = new THREE.Matrix4().makeBasis(xAxis, yAxis, zAxis);
    const quaternion = new THREE.Quaternion().setFromRotationMatrix(rotMatrix);

    const segments = 32;
    const mainLength = length * 0.875;
    const tipLength = length * 0.125;
    const opacity = isComparison ? 0.5 : isFrozen ? 0.25 : isTranslucent ? 0.5 : 1;
    const color = isComparison ? colors.slate[400] : isFrozen ? colors.slate[300] : colors.slate[50];

    return (
        <group position={center} quaternion={quaternion}>
            <mesh position-y={tipLength / 2}>
                <cylinderGeometry args={[radius * 0.3, radius, mainLength, segments, segments, openEnded]} />
                <meshStandardMaterial color={color} flatShading opacity={opacity} transparent />
            </mesh>

            <mesh position-y={-mainLength / 2}>
                <cylinderGeometry args={[radius, 0, tipLength, segments, segments, openEnded]} />
                <meshStandardMaterial color={color} flatShading opacity={opacity} transparent />
            </mesh>
        </group>
    );
};
